/**
 * ReportsTVPage — Full-screen production scoreboard for print + packing rooms.
 *
 * Renders today's running totals (printed, defect rate, defects, orders)
 * in a 2×2 grid sized for a wall-mounted TV viewed from 3–5 metres. Compares
 * each metric against the same weekday one week ago. Auto-refreshes every
 * 60s; auto-rolls at midnight in the location's timezone.
 *
 * Entry: TV Mode button on QAReportsPage. Exit: Esc or close button.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  X,
  Printer,
  BarChart3,
  AlertTriangle,
  ClipboardList,
  Building2,
  TrendingUp,
  TrendingDown,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from '@/contexts/LocationContext';
import { cn } from '@/lib/utils';
import { METRIC_COLORS, type MetricKey } from '../metricColors';
import { useReportsTVData } from '../hooks/useReportsTVData';

const REPORTS_PATH = '/production/reports';

interface TVMetricCardProps {
  label: string;
  value: string;
  metricKey: MetricKey;
  icon: React.ComponentType<{ className?: string }>;
  delta: { percent: number | null; prior: number };
  /** True for defect-y metrics: down = good, up = bad. */
  inverted?: boolean;
  /** "vs last Wednesday" — the comparison subtitle prefix. */
  comparisonLabel: string;
  /** Pre-formatted absolute prior value for display, e.g. "1,950" or "2.6%". */
  priorFormatted: string;
  /**
   * Whether the comparison block (delta chip + "vs last X" subtitle) should
   * render at all. False when both today and the prior week are zero — saves
   * the card from reading "nothing vs nothing" on quiet days.
   */
  hasComparison: boolean;
}

function TVMetricCard({
  label,
  value,
  metricKey,
  icon: Icon,
  delta,
  inverted,
  comparisonLabel,
  priorFormatted,
  hasComparison,
}: TVMetricCardProps) {
  const color = METRIC_COLORS[metricKey];
  const hasDelta = delta.percent !== null;
  const isPositiveChange = (delta.percent ?? 0) > 0;
  const isGood = inverted ? !isPositiveChange : isPositiveChange;

  return (
    <div
      className={cn(
        'relative flex flex-col justify-between overflow-hidden rounded-2xl border bg-card p-[clamp(1.5rem,3vw,3rem)]',
        // Left-edge accent bar in the metric's colour — same trick as MetricCard
        "before:absolute before:inset-y-0 before:left-0 before:w-[6px] before:content-['']",
        color.ring,
      )}
    >
      {/* Top: label + icon chip */}
      <div className="flex items-start justify-between gap-4">
        <span className="text-[clamp(1rem,1.6vw,1.75rem)] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-xl',
            'h-[clamp(2.5rem,4vw,4rem)] w-[clamp(2.5rem,4vw,4rem)]',
            color.bg,
          )}
          aria-hidden="true"
        >
          <Icon className={cn('h-[60%] w-[60%]', color.text)} />
        </span>
      </div>

      {/* Middle: the big number. Capped lower than the natural 14vw so a
          long token like "0.0%" doesn't visually dwarf single-digit values
          on the sibling cards. */}
      <div className="flex-1 flex items-center min-h-0">
        <div className="text-[clamp(4rem,11vw,13rem)] font-bold tabular-nums leading-none tracking-tight">
          {value}
        </div>
      </div>

      {/* Bottom: delta + comparison. Suppressed entirely when both today
          and last-week are zero — avoids the lonely em-dash. */}
      {hasComparison && (
        <div className="space-y-2">
          {hasDelta && (
            <div
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-[clamp(0.75rem,1.2vw,1.25rem)] py-[clamp(0.25rem,0.6vw,0.5rem)] text-[clamp(1rem,1.5vw,1.75rem)] font-semibold tabular-nums',
                isGood
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                  : 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
              )}
            >
              {isPositiveChange ? (
                <TrendingUp className="h-[1em] w-[1em]" />
              ) : (
                <TrendingDown className="h-[1em] w-[1em]" />
              )}
              {isPositiveChange ? '+' : ''}
              {(delta.percent ?? 0).toFixed(1)}%
            </div>
          )}
          <p className="text-[clamp(0.875rem,1.2vw,1.25rem)] text-muted-foreground">
            {comparisonLabel} <span className="text-foreground/80 font-medium">{priorFormatted}</span>
          </p>
        </div>
      )}
    </div>
  );
}

export function ReportsTVPage() {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { selectedLocation } = useLocation();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(REPORTS_PATH);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const data = useReportsTVData(selectedLocation?.id, selectedLocation?.timezone);

  // Fixed exit button — always visible, mirrors the bin dashboard's TV button.
  const ExitButton = (
    <Button
      variant="ghost"
      size="icon"
      className="fixed top-3 right-3 z-50 h-10 w-10 opacity-40 hover:opacity-100 transition-opacity"
      onClick={() => navigate(REPORTS_PATH)}
      title={t('qa.exitTvMode')}
      aria-label={t('qa.exitTvMode')}
    >
      <X className="h-6 w-6" />
    </Button>
  );

  // Location guard — match the QAReports page tone.
  if (!selectedLocation) {
    return (
      <div className="min-h-screen w-screen bg-background flex items-center justify-center p-8">
        {ExitButton}
        <div className="flex flex-col items-center text-center max-w-md">
          <Building2 className="h-16 w-16 text-muted-foreground mb-6" />
          <h2 className="text-3xl font-semibold mb-3">{t('qa.tvSelectLocation')}</h2>
          <p className="text-lg text-muted-foreground">{t('qa.tvSelectLocationDesc')}</p>
        </div>
      </div>
    );
  }

  const todayLabel = format(data.today_date, 'EEEE, MMMM d');
  const comparisonLabel = t('qa.tvVsLastWeekday', { weekday: data.lastWeekdayLabel });

  // Per-metric comparison gating: suppress the bottom block when both today
  // and last week are zero, so a fresh-org / quiet-day card just shows the
  // headline number without a lonely em-dash underneath.
  const showComparison = (current: number, prior: number) =>
    current > 0 || prior > 0;

  return (
    <div className="h-screen w-screen overflow-hidden bg-background flex flex-col p-[clamp(1.5rem,3vw,3.5rem)]">
      {ExitButton}

      {/* Top bar: location · today · live dot. Sized small relative to the grid
          so the cards dominate the screen. */}
      <header className="flex items-center justify-between gap-6 pb-[clamp(1rem,1.75vw,2rem)]">
        <div className="flex items-center gap-3 text-[clamp(1rem,1.5vw,1.5rem)]">
          <MapPin className="h-[1em] w-[1em] text-muted-foreground" />
          <span className="font-semibold">{selectedLocation.name}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            {t('qa.tvToday')} - <span className="text-foreground">{todayLabel}</span>
          </span>
        </div>
        <div className="flex items-center gap-3 text-[clamp(0.875rem,1.2vw,1.25rem)] text-muted-foreground pr-12">
          {/* Pulsing live dot — confirms the screen is actually refreshing. */}
          <span className="relative inline-flex h-3 w-3" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
          </span>
          <span>{t('qa.tvLiveIndicator')}</span>
        </div>
      </header>

      {/* 2x2 grid filling the remaining viewport. `min-h-0` lets the grid
          shrink inside its flex parent so the cards never push past the fold. */}
      <main className="flex-1 min-h-0 grid grid-cols-2 grid-rows-2 gap-[clamp(1rem,2vw,2rem)]">
        <TVMetricCard
          label={t('qa.stats.totalPrints')}
          value={data.today.itemsPrinted.toLocaleString()}
          metricKey="itemsPrinted"
          icon={Printer}
          delta={data.deltas.itemsPrinted}
          comparisonLabel={comparisonLabel}
          priorFormatted={data.lastWeek.itemsPrinted.toLocaleString()}
          hasComparison={showComparison(data.today.itemsPrinted, data.lastWeek.itemsPrinted)}
        />
        <TVMetricCard
          label={t('qa.stats.defectRate')}
          value={`${data.today.defectRate.toFixed(1)}%`}
          metricKey="defectRate"
          icon={BarChart3}
          delta={data.deltas.defectRate}
          inverted
          comparisonLabel={comparisonLabel}
          priorFormatted={`${data.lastWeek.defectRate.toFixed(1)}%`}
          hasComparison={showComparison(data.today.defectRate, data.lastWeek.defectRate)}
        />
        <TVMetricCard
          label={t('qa.stats.totalDefects')}
          value={data.today.totalDefects.toLocaleString()}
          metricKey="defects"
          icon={AlertTriangle}
          delta={data.deltas.totalDefects}
          inverted
          comparisonLabel={comparisonLabel}
          priorFormatted={data.lastWeek.totalDefects.toLocaleString()}
          hasComparison={showComparison(data.today.totalDefects, data.lastWeek.totalDefects)}
        />
        <TVMetricCard
          label={t('qa.stats.totalOrders')}
          value={data.today.totalOrders.toLocaleString()}
          metricKey="totalOrders"
          icon={ClipboardList}
          delta={data.deltas.totalOrders}
          comparisonLabel={comparisonLabel}
          priorFormatted={data.lastWeek.totalOrders.toLocaleString()}
          hasComparison={showComparison(data.today.totalOrders, data.lastWeek.totalOrders)}
        />
      </main>
    </div>
  );
}
